<template>
  <div class="hms-page">
    <Navigation />
    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.back()" class="hms-back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-baby-face me-2"></i>{{ $t('page.newborn', 'Birth & Life Record') }}</h4>
        </div>
        <button class="hms-btn hms-btn-blue" @click="load"><i class="mdi mdi-refresh"></i></button>
      </div>

      <div v-if="!record.patient" class="hms-empty">
        <i class="mdi mdi-baby-face-outline"></i>
        <h5>Loading…</h5>
      </div>

      <div v-else>
        <!-- TABS -->
        <div class="hms-tab-row">
          <button class="hms-tab" :class="{ active: tab==='birth' }" @click="tab='birth'">
            <i class="mdi mdi-baby-carriage"></i> Birth & family
          </button>
          <button class="hms-tab" :class="{ active: tab==='growth' }" @click="tab='growth'; loadGrowth()">
            <i class="mdi mdi-chart-bell-curve"></i> Growth
            <span v-if="growthFlag" class="hms-pill ms-1" :class="growthFlag==='danger' ? 'hms-pill-danger' : (growthFlag==='faltering' ? 'hms-pill-warn' : 'hms-pill-success')">{{ growthFlag }}</span>
          </button>
          <button class="hms-tab" :class="{ active: tab==='milestones' }" @click="tab='milestones'; loadMilestones()">
            <i class="mdi mdi-puzzle"></i> Milestones
            <span v-if="redFlagCount" class="hms-pill hms-pill-danger ms-1">{{ redFlagCount }}</span>
          </button>
          <button class="hms-tab" :class="{ active: tab==='kepi' }" @click="tab='kepi'; loadKepi()">
            <i class="mdi mdi-needle"></i> Immunization
            <span v-if="kepiOverdueCount" class="hms-pill hms-pill-warn ms-1">{{ kepiOverdueCount }}</span>
          </button>
          <button class="hms-tab" :class="{ active: tab==='imci' }" @click="tab='imci'; loadImci()">
            <i class="mdi mdi-stethoscope"></i> Sick visit (IMCI)
          </button>
          <button class="hms-tab" :class="{ active: tab==='nutrition' }" @click="tab='nutrition'; loadSam()">
            <i class="mdi mdi-food-apple"></i> Nutrition (SAM/RUTF)
            <span v-if="sam.case && sam.case.status==='active'" class="hms-pill hms-pill-warn ms-1">on RUTF</span>
          </button>
        </div>

        <!-- BIRTH & FAMILY TAB (existing content) -->
        <div v-if="tab==='birth'">
        <!-- HEADER STRIP -->
        <div class="hms-card mb-3" style="padding:16px">
          <div class="hms-grid-2-1" style="align-items:center">
            <div>
              <h3 style="margin:0; font-size:22px">
                {{ record.patient.first_name }} {{ record.patient.last_name }}
              </h3>
              <div class="hms-muted-sm" style="margin-top:4px">
                {{ record.patient.patient_no }} · DOB {{ formatDate(record.patient.dob) }} ({{ ageDisplay }}) · {{ record.patient.gender }}
              </div>
              <div v-if="record.patient.birth_notification_no" class="hms-muted-sm" style="margin-top:2px">
                <i class="mdi mdi-certificate-outline"></i> Notification of Birth: <strong>{{ record.patient.birth_notification_no }}</strong>
              </div>
              <div v-if="record.patient.is_hei_baby==1" class="hms-pill hms-pill-warn" style="margin-top:6px">
                <i class="mdi mdi-virus"></i> HIV-exposed infant — on PMTCT follow-up
              </div>
            </div>
            <div class="text-end">
              <a class="hms-btn hms-btn-blue hms-btn-sm" :href="'#/patient/' + patientId + '/timeline'">
                <i class="mdi mdi-timeline-text-outline"></i> Full life timeline
              </a>
              <button v-if="record.patient.mother_patient_id" class="hms-btn hms-btn-secondary hms-btn-sm ms-2" @click="$router.push('/patients/' + record.patient.mother_patient_id)">
                <i class="mdi mdi-mother-heart"></i> Open mother's EMR
              </button>
            </div>
          </div>
        </div>

        <div class="hms-grid-2">
          <!-- LEFT COLUMN: Birth + Delivery -->
          <div>
            <div class="hms-card mb-3">
              <div class="hms-card-head"><i class="mdi mdi-baby-carriage"></i> Birth event</div>
              <div class="hms-card-body">
                <div v-if="record.delivery">
                  <div class="hms-row"><span class="hms-row-k">Date / time</span><span class="hms-row-v">{{ formatDateTime(record.delivery.delivered_at) }}</span></div>
                  <div class="hms-row"><span class="hms-row-k">Mode</span><span class="hms-row-v">{{ record.delivery.mode }}</span></div>
                  <div class="hms-row"><span class="hms-row-k">Presentation</span><span class="hms-row-v">{{ record.delivery.presenting_part }}</span></div>
                  <div class="hms-row"><span class="hms-row-k">Sex</span><span class="hms-row-v">{{ record.delivery.baby_sex || '—' }}</span></div>
                  <div class="hms-row"><span class="hms-row-k">Birth weight</span><span class="hms-row-v">{{ record.delivery.baby_weight_g ? record.delivery.baby_weight_g + ' g' : '—' }} <span v-if="lowBirthWeight" class="hms-pill hms-pill-warn">LBW</span></span></div>
                  <div class="hms-row"><span class="hms-row-k">Length</span><span class="hms-row-v">{{ record.delivery.baby_length_cm ? record.delivery.baby_length_cm + ' cm' : '—' }}</span></div>
                  <div class="hms-row"><span class="hms-row-k">Head circ</span><span class="hms-row-v">{{ record.delivery.head_circumference_cm ? record.delivery.head_circumference_cm + ' cm' : '—' }}</span></div>
                  <div class="hms-row"><span class="hms-row-k">APGAR 1 / 5</span><span class="hms-row-v">{{ record.delivery.apgar_1 || '?' }} / {{ record.delivery.apgar_5 || '?' }}</span></div>
                  <div class="hms-row"><span class="hms-row-k">Cried immediately</span><span class="hms-row-v"><i class="mdi" :class="record.delivery.cried_immediately==1 ? 'mdi-check-circle hms-good' : 'mdi-close-circle hms-bad'"></i></span></div>
                  <div class="hms-row"><span class="hms-row-k">Resuscitation</span><span class="hms-row-v">{{ record.delivery.resuscitation || 'none' }}</span></div>
                  <div class="hms-row"><span class="hms-row-k">Place</span><span class="hms-row-v">{{ record.patient.birth_facility_name || '—' }}</span></div>
                  <div class="hms-row"><span class="hms-row-k">Delivered by</span><span class="hms-row-v">{{ record.delivery.delivered_by_name || '—' }}</span></div>
                </div>
                <div v-else class="hms-muted">No delivery record linked.</div>
              </div>
            </div>

            <div class="hms-card mb-3">
              <div class="hms-card-head"><i class="mdi mdi-needle"></i> First-day care</div>
              <div class="hms-card-body">
                <div v-if="record.delivery">
                  <div class="hms-row"><span class="hms-row-k">BF within 1h</span><span class="hms-row-v"><i class="mdi" :class="record.delivery.bf_within_1hr==1 ? 'mdi-check-circle hms-good' : 'mdi-close-circle hms-bad'"></i></span></div>
                  <div class="hms-row"><span class="hms-row-k">Vit K given</span><span class="hms-row-v"><i class="mdi" :class="record.delivery.vit_k_given==1 ? 'mdi-check-circle hms-good' : 'mdi-close-circle hms-bad'"></i></span></div>
                  <div class="hms-row"><span class="hms-row-k">Tetracycline eye</span><span class="hms-row-v"><i class="mdi" :class="record.delivery.tetracycline_eye==1 ? 'mdi-check-circle hms-good' : 'mdi-close-circle hms-bad'"></i></span></div>
                  <div class="hms-row"><span class="hms-row-k">BCG given</span><span class="hms-row-v"><i class="mdi" :class="record.delivery.bcg_given==1 ? 'mdi-check-circle hms-good' : 'mdi-close-circle hms-bad'"></i></span></div>
                  <div class="hms-row"><span class="hms-row-k">OPV-0 given</span><span class="hms-row-v"><i class="mdi" :class="record.delivery.opv_given==1 ? 'mdi-check-circle hms-good' : 'mdi-close-circle hms-bad'"></i></span></div>
                  <div class="hms-row"><span class="hms-row-k">NICU admitted</span><span class="hms-row-v">
                    <i class="mdi" :class="record.delivery.nicu_admission==1 ? 'mdi-alert hms-bad' : 'mdi-check-circle hms-good'"></i>
                    <span v-if="record.delivery.nicu_admission==1" class="hms-muted-sm">{{ record.delivery.nicu_reason || '' }}</span>
                  </span></div>
                </div>
              </div>
            </div>
          </div>

          <!-- RIGHT COLUMN: Mother context + HEI + Family -->
          <div>
            <div class="hms-card mb-3">
              <div class="hms-card-head"><i class="mdi mdi-mother-heart"></i> Mother (at time of birth)</div>
              <div class="hms-card-body">
                <div class="hms-row"><span class="hms-row-k">Name</span><span class="hms-row-v"><strong>{{ record.patient.mother_name || '—' }}</strong></span></div>
                <div class="hms-row"><span class="hms-row-k">Mother ID</span><span class="hms-row-v">{{ record.patient.mother_patient_no || '—' }}</span></div>
                <div class="hms-row"><span class="hms-row-k">Phone</span><span class="hms-row-v">{{ record.patient.mother_phone || '—' }}</span></div>
                <div v-if="record.mother_anc_latest">
                  <div class="hms-row"><span class="hms-row-k">Last ANC</span><span class="hms-row-v">visit #{{ record.mother_anc_latest.visit_number }} · GA {{ record.mother_anc_latest.gestational_weeks }}w</span></div>
                  <div class="hms-row"><span class="hms-row-k">Risk at last ANC</span><span class="hms-row-v"><span class="hms-pill" :class="riskPill(record.mother_anc_latest.risk_category)">{{ record.mother_anc_latest.risk_category }} ({{ record.mother_anc_latest.risk_score }})</span></span></div>
                </div>
              </div>
            </div>

            <div v-if="record.mother_pmtct" class="hms-card mb-3 hms-card-pmtct">
              <div class="hms-card-head"><i class="mdi mdi-virus"></i> Mother PMTCT context</div>
              <div class="hms-card-body">
                <div class="hms-row"><span class="hms-row-k">HIV status</span><span class="hms-row-v"><span class="hms-pill hms-pill-info">{{ record.mother_pmtct.hiv_status }}</span></span></div>
                <div class="hms-row"><span class="hms-row-k">ART regimen</span><span class="hms-row-v">{{ record.mother_pmtct.art_regimen || '—' }}</span></div>
                <div class="hms-row"><span class="hms-row-k">Suppressed at delivery</span>
                  <span class="hms-row-v">
                    <span v-if="record.mother_pmtct.suppressed_at_delivery==1" class="hms-pill hms-pill-success">YES (&lt;1000 c/ml)</span>
                    <span v-else class="hms-pill hms-pill-danger">NO — high transmission risk</span>
                  </span>
                </div>
                <div class="hms-row"><span class="hms-row-k">Cascade stage</span><span class="hms-row-v">{{ (record.mother_pmtct.cascade_stage || '').replace(/_/g,' ') }}</span></div>
              </div>
            </div>

            <div v-if="record.hei" class="hms-card mb-3">
              <div class="hms-card-head"><i class="mdi mdi-baby-face-outline"></i> HEI (HIV-exposed infant) follow-up</div>
              <div class="hms-card-body">
                <div class="hms-row"><span class="hms-row-k">Risk</span>
                  <span class="hms-row-v">
                    <span class="hms-pill" :class="record.hei.risk_classification==='high_risk' ? 'hms-pill-danger' : 'hms-pill-info'">{{ record.hei.risk_classification.replace(/_/g,' ') }}</span>
                  </span>
                </div>
                <div class="hms-row"><span class="hms-row-k">ARV regimen</span><span class="hms-row-v">{{ record.hei.arv_regimen }}</span></div>
                <div class="hms-row"><span class="hms-row-k">Feeding</span><span class="hms-row-v">{{ (record.hei.feeding_option || '').replace(/_/g,' ') }}</span></div>
                <div class="hms-row"><span class="hms-row-k">Final status</span><span class="hms-row-v"><span class="hms-pill" :class="heiStatusPill(record.hei.final_status)">{{ (record.hei.final_status || '').replace(/_/g,' ') }}</span></span></div>
                <table class="hms-table" style="margin-top:8px">
                  <thead><tr><th>Test</th><th>Due</th><th>Sample</th><th>Result</th></tr></thead>
                  <tbody>
                    <tr v-for="t in record.hei_tests" :key="t.dbs_id">
                      <td>{{ t.test_type.replace(/_/g,' ') }}</td>
                      <td>{{ formatDate(t.scheduled_date) }}</td>
                      <td>{{ formatDate(t.sample_taken_date) || '—' }}</td>
                      <td><span class="hms-pill" :class="dbsResultPill(t.result)">{{ t.result }}</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="hms-card mb-3">
              <div class="hms-card-head"><i class="mdi mdi-account-group"></i> Family</div>
              <div class="hms-card-body">
                <div v-if="record.parents && record.parents.length">
                  <h6>Parents / guardians</h6>
                  <div v-for="p in record.parents" :key="p.rel_id" class="hms-fam-row">
                    <span class="hms-pill hms-pill-info">{{ (p.relationship_type || '').replace(/_/g,' ') }}</span>
                    <span><strong>{{ p.parent_name }}</strong> <span class="hms-muted-sm">{{ p.parent_patient_no }}</span></span>
                    <span class="hms-muted-sm">{{ p.parent_phone }}</span>
                  </div>
                </div>
                <div v-if="record.siblings && record.siblings.length" class="mt-3">
                  <h6>Siblings</h6>
                  <div v-for="s in record.siblings" :key="s.patient_id" class="hms-fam-row">
                    <span>{{ s.first_name }} {{ s.last_name }}</span>
                    <span class="hms-muted-sm">{{ formatDate(s.dob) }}, {{ s.gender }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        <!-- ============ GROWTH TAB ============ -->
        <div v-if="tab==='growth'">
          <div class="d-flex gap-2 mb-3 align-items-center">
            <select v-model="growthIndicator" class="hms-input" style="max-width:240px">
              <option value="weight_for_age">Weight-for-age</option>
              <option value="length_for_age">Length/Height-for-age</option>
              <option value="head_circ_for_age">Head circumference</option>
            </select>
            <button class="hms-btn hms-btn-blue" @click="openGrowthModal"><i class="mdi mdi-plus"></i> Record measurement</button>
          </div>

          <!-- Growth chart -->
          <div class="hms-card mb-3" v-if="growth.measurements && growth.measurements.length">
            <div class="hms-card-head"><i class="mdi mdi-chart-bell-curve"></i> {{ indicatorLabel(growthIndicator) }} — WHO {{ growth.patient && growth.patient.gender === 'male' ? 'boys' : 'girls' }} standard</div>
            <div class="hms-card-body">
              <svg :viewBox="'0 0 700 360'" class="hms-growth-svg">
                <!-- y-axis grid + labels -->
                <g v-for="i in 6" :key="'yg'+i">
                  <line :x1="40" :x2="680" :y1="30 + i*50" :y2="30 + i*50" stroke="#edf2f7" />
                  <text :x="34" :y="33 + i*50" font-size="9" text-anchor="end" fill="#718096">{{ yLabel(i) }}</text>
                </g>
                <!-- axes -->
                <line x1="40" x2="40" y1="20" y2="330" stroke="#94a3b8" />
                <line x1="40" x2="680" y1="330" y2="330" stroke="#94a3b8" />
                <!-- x labels (months) -->
                <text v-for="m in [0,6,12,18,24,30,36,42,48,54,60]" :key="'xl'+m"
                      :x="40 + (m/60)*640" y="346" font-size="9" text-anchor="middle" fill="#4a5568">{{ m }}m</text>
                <!-- WHO bands -->
                <path :d="bandPath('upper')" fill="#fed7d7" fill-opacity="0.35" stroke="none" />
                <path :d="bandPath('lower')" fill="#fed7d7" fill-opacity="0.35" stroke="none" />
                <polyline :points="refLine('p2')" fill="none" stroke="#fc8181" stroke-width="1" stroke-dasharray="3 3" />
                <polyline :points="refLine('med')" fill="none" stroke="#22c55e" stroke-width="1.5" />
                <polyline :points="refLine('m2')" fill="none" stroke="#fc8181" stroke-width="1" stroke-dasharray="3 3" />
                <!-- measurement points + connecting line -->
                <polyline :points="measurementLine" fill="none" stroke="#2b6cb0" stroke-width="2" />
                <g v-for="m in growth.measurements" :key="m.gm_id">
                  <circle :cx="xCoord(m.age_months_at_visit)" :cy="yCoord(measurementValue(m))" r="4" fill="#2b6cb0" stroke="#fff" stroke-width="1.5" />
                </g>
                <!-- legend -->
                <g transform="translate(450, 20)">
                  <line x1="0" x2="20" y1="6" y2="6" stroke="#22c55e" stroke-width="1.5" />
                  <text x="26" y="9" font-size="9" fill="#4a5568">Median</text>
                  <line x1="80" x2="100" y1="6" y2="6" stroke="#fc8181" stroke-dasharray="3 3" />
                  <text x="106" y="9" font-size="9" fill="#4a5568">±2 SD</text>
                  <circle cx="160" cy="6" r="3" fill="#2b6cb0" />
                  <text x="168" y="9" font-size="9" fill="#4a5568">Measurement</text>
                </g>
              </svg>
            </div>
          </div>

          <!-- Measurements table -->
          <table class="hms-table">
            <thead><tr><th>When</th><th>Age</th><th>Weight</th><th>Length/Ht</th><th>HC</th><th>MUAC</th><th>WAZ</th><th>HAZ</th><th>Status</th><th>By</th></tr></thead>
            <tbody>
              <tr v-for="m in growth.measurements" :key="m.gm_id" :class="growthRowClass(m)">
                <td>{{ formatDate(m.measured_at) }}</td>
                <td class="hms-muted-sm">{{ ageDisplayMonths(m.age_months_at_visit) }}</td>
                <td>{{ m.weight_kg ? m.weight_kg + ' kg' : '—' }}</td>
                <td>{{ (m.length_cm || m.height_cm) ? (m.length_cm || m.height_cm) + ' cm' : '—' }}</td>
                <td>{{ m.head_circumference_cm ? m.head_circumference_cm + ' cm' : '—' }}</td>
                <td>{{ m.muac_cm ? m.muac_cm + ' cm' : '—' }}</td>
                <td><span :class="zPill(m.waz)">{{ m.waz !== null ? m.waz : '—' }}</span></td>
                <td><span :class="zPill(m.haz)">{{ m.haz !== null ? m.haz : '—' }}</span></td>
                <td><span class="hms-pill" :class="statusPill(m.nutritional_status)">{{ (m.nutritional_status || '').replace(/_/g,' ') }}</span></td>
                <td class="hms-muted-sm">{{ (m.source || '').replace(/_/g,' ') }}</td>
              </tr>
              <tr v-if="!growth.measurements || !growth.measurements.length"><td colspan="10" class="text-center hms-pad hms-muted">No growth measurements recorded.</td></tr>
            </tbody>
          </table>

          <!-- Growth modal -->
          <div v-if="growthModal" class="hms-modal-bg" @click.self="growthModal=false">
            <div class="hms-modal">
              <div class="hms-modal-head">
                <h5><i class="mdi mdi-scale-bathroom"></i> Record growth measurement</h5>
                <button class="hms-btn hms-btn-secondary" @click="growthModal=false"><i class="mdi mdi-close"></i></button>
              </div>
              <div class="hms-modal-body">
                <div class="hms-form-grid">
                  <label>Date/time <input type="datetime-local" v-model="growthForm.measured_at" class="hms-input" /></label>
                  <label>Weight (kg) <input type="number" step="0.01" v-model="growthForm.weight_kg" class="hms-input" /></label>
                  <label>Length (cm, &lt;2y) <input type="number" step="0.1" v-model="growthForm.length_cm" class="hms-input" /></label>
                  <label>Height (cm, ≥2y) <input type="number" step="0.1" v-model="growthForm.height_cm" class="hms-input" /></label>
                  <label>Head circ (cm) <input type="number" step="0.1" v-model="growthForm.head_circumference_cm" class="hms-input" /></label>
                  <label>MUAC (cm) <input type="number" step="0.1" v-model="growthForm.muac_cm" class="hms-input" /></label>
                  <label>Source
                    <select v-model="growthForm.source" class="hms-input">
                      <option value="facility">Facility</option>
                      <option value="home_visit">Home visit</option>
                      <option value="community">Community</option>
                      <option value="school">School</option>
                    </select>
                  </label>
                </div>
                <label class="d-block mt-2">Notes <textarea v-model="growthForm.notes" rows="2" class="hms-input"></textarea></label>
              </div>
              <div class="hms-modal-foot">
                <button class="hms-btn hms-btn-secondary" @click="growthModal=false">Cancel</button>
                <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveGrowth">{{ saving ? 'Saving…' : 'Save measurement' }}</button>
              </div>
            </div>
          </div>
        </div>

        <!-- ============ MILESTONES TAB ============ -->
        <div v-if="tab==='milestones'">
          <div class="hms-muted-sm mb-2">Child age: <strong>{{ ageDisplay }}</strong>. Click a milestone to record. Red-flag = past expected age and still not achieved.</div>

          <div v-for="domain in milestoneDomains" :key="domain.key" class="hms-card mb-3">
            <div class="hms-card-head"><i :class="domain.icon"></i> {{ domain.label }}</div>
            <div class="hms-card-body">
              <table class="hms-table">
                <thead><tr><th>Milestone</th><th>Typical</th><th>Red-flag</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="m in milestonesByDomain(domain.key)" :key="m.code" :class="milestoneRowClass(m)">
                    <td>{{ m.label }}</td>
                    <td class="hms-muted-sm">{{ m.typical_age_months }} mo</td>
                    <td class="hms-muted-sm">{{ m.red_flag_age_months }} mo</td>
                    <td>
                      <span class="hms-pill" :class="milestonePill(m.effective_status)">{{ (m.effective_status || '').replace(/_/g,' ') }}</span>
                      <div v-if="m.age_months_at_check" class="hms-muted-sm">@ {{ m.age_months_at_check }} mo</div>
                    </td>
                    <td>
                      <button class="hms-btn hms-btn-success hms-btn-sm" @click="setMilestone(m.code, 'achieved')" title="Mark achieved"><i class="mdi mdi-check"></i></button>
                      <button class="hms-btn hms-btn-warning hms-btn-sm ms-1" @click="setMilestone(m.code, 'concern')" title="Concern"><i class="mdi mdi-alert"></i></button>
                      <button class="hms-btn hms-btn-secondary hms-btn-sm ms-1" @click="setMilestone(m.code, 'not_yet')" title="Not yet"><i class="mdi mdi-clock-outline"></i></button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- ============ IMCI TAB ============ -->
        <div v-if="tab==='imci'">
          <div class="d-flex gap-2 mb-3 align-items-center">
            <button class="hms-btn hms-btn-blue" @click="openImciModal"><i class="mdi mdi-plus"></i> New IMCI assessment</button>
            <span class="hms-muted-sm">Child age: <strong>{{ ageDisplay }}</strong> — band: {{ imciAgeBand }}</span>
          </div>

          <div v-for="a in imci" :key="a.imci_id" class="hms-card mb-3" :class="imciCardClass(a)">
            <div class="hms-card-head">
              <span><i class="mdi mdi-clipboard-pulse"></i> {{ formatDateTime(a.assessed_at) }} · age {{ a.age_months_at_visit }}mo · <strong>{{ a.final_action.replace(/_/g,' ') }}</strong></span>
              <span class="hms-muted-sm">{{ a.assessed_by_name || '' }}</span>
            </div>
            <div class="hms-card-body">
              <div v-if="a.danger_signs && a.danger_signs !== 'none'" class="hms-imci-row hms-imci-danger">
                <i class="mdi mdi-alert"></i> <strong>Danger signs:</strong> {{ a.danger_signs.replace(/,/g,' · ').replace(/_/g,' ') }}
              </div>
              <div class="hms-imci-grid">
                <div v-if="a.cough_present==1"><strong>Cough:</strong> <span class="hms-pill" :class="imciPill(a.cough_classification)">{{ a.cough_classification.replace(/_/g,' ') }}</span> <span class="hms-muted-sm">RR={{ a.respiratory_rate || '—' }}</span></div>
                <div v-if="a.diarrhoea_present==1"><strong>Diarrhoea:</strong> <span class="hms-pill" :class="imciPill(a.diarrhoea_classification)">{{ a.diarrhoea_classification.replace(/_/g,' ') }}</span></div>
                <div v-if="a.fever_present==1"><strong>Fever:</strong> <span class="hms-pill" :class="imciPill(a.fever_classification)">{{ a.fever_classification.replace(/_/g,' ') }}</span> <span class="hms-muted-sm">{{ a.temperature_c ? a.temperature_c + '°C' : '' }}</span></div>
                <div v-if="a.ear_problem==1"><strong>Ear:</strong> <span class="hms-pill" :class="imciPill(a.ear_classification)">{{ a.ear_classification.replace(/_/g,' ') }}</span></div>
                <div v-if="a.anaemia_classification !== 'not_assessed' && a.anaemia_classification !== 'no_anaemia'"><strong>Anaemia:</strong> <span class="hms-pill" :class="imciPill(a.anaemia_classification)">{{ a.anaemia_classification.replace(/_/g,' ') }}</span></div>
                <div v-if="a.nutrition_classification !== 'not_assessed'"><strong>Nutrition:</strong> <span class="hms-pill" :class="imciPill(a.nutrition_classification)">{{ a.nutrition_classification.replace(/_/g,' ') }}</span></div>
              </div>
              <div v-if="a.treatments_given" class="hms-muted-sm mt-2"><strong>Tx:</strong> {{ a.treatments_given.replace(/,/g,' · ').replace(/_/g,' ') }}</div>
              <div v-if="a.notes" class="hms-muted-sm mt-1">{{ a.notes }}</div>
              <div v-if="a.follow_up_date" class="hms-muted-sm">Next visit: <strong>{{ formatDate(a.follow_up_date) }}</strong></div>
            </div>
          </div>
          <div v-if="!imci.length" class="hms-muted text-center hms-pad">No IMCI assessments yet.</div>

          <!-- IMCI modal -->
          <div v-if="imciModal" class="hms-modal-bg" @click.self="imciModal=false">
            <div class="hms-modal" style="width:760px">
              <div class="hms-modal-head">
                <h5><i class="mdi mdi-stethoscope"></i> IMCI assessment</h5>
                <button class="hms-btn hms-btn-secondary" @click="imciModal=false"><i class="mdi mdi-close"></i></button>
              </div>
              <div class="hms-modal-body">
                <div class="hms-form-grid">
                  <label>Assessed at <input type="datetime-local" v-model="imciForm.assessed_at" class="hms-input" /></label>
                  <label>Weight (kg) <input type="number" step="0.01" v-model="imciForm.weight_kg" class="hms-input" /></label>
                  <label>Temp (°C) <input type="number" step="0.1" v-model="imciForm.temperature_c" class="hms-input" /></label>
                  <label>Final action
                    <select v-model="imciForm.final_action" class="hms-input">
                      <option value="">(auto-classify)</option>
                      <option value="home_treatment">Home treatment</option>
                      <option value="return_in_2_days">Return in 2 days</option>
                      <option value="return_in_5_days">Return in 5 days</option>
                      <option value="urgent_referral">Urgent referral</option>
                      <option value="admit_inpatient">Admit inpatient</option>
                    </select>
                  </label>
                </div>

                <h6 class="mt-3 hms-imci-section"><i class="mdi mdi-alert-octagon"></i> General danger signs (any → severe)</h6>
                <div class="hms-chip-row">
                  <label v-for="opt in dangerSignsOpts" :key="opt" class="hms-chip">
                    <input type="checkbox" :value="opt" v-model="imciDangerArr" /> {{ opt.replace(/_/g,' ') }}
                  </label>
                </div>

                <h6 class="mt-3 hms-imci-section"><i class="mdi mdi-lungs"></i> Cough / difficulty breathing</h6>
                <label class="hms-check"><input type="checkbox" v-model="imciForm.cough_present" :true-value="1" :false-value="0" /> Cough present</label>
                <div v-if="imciForm.cough_present==1" class="hms-form-grid">
                  <label>Days <input type="number" v-model="imciForm.cough_days" class="hms-input" /></label>
                  <label>RR (per min) <input type="number" v-model="imciForm.respiratory_rate" class="hms-input" /></label>
                  <label class="hms-check"><input type="checkbox" v-model="imciForm.chest_indrawing" :true-value="1" :false-value="0" /> Chest indrawing</label>
                  <label class="hms-check"><input type="checkbox" v-model="imciForm.stridor" :true-value="1" :false-value="0" /> Stridor</label>
                </div>

                <h6 class="mt-3 hms-imci-section"><i class="mdi mdi-water"></i> Diarrhoea</h6>
                <label class="hms-check"><input type="checkbox" v-model="imciForm.diarrhoea_present" :true-value="1" :false-value="0" /> Diarrhoea</label>
                <div v-if="imciForm.diarrhoea_present==1">
                  <div class="hms-form-grid">
                    <label>Days <input type="number" v-model="imciForm.diarrhoea_days" class="hms-input" /></label>
                    <label class="hms-check"><input type="checkbox" v-model="imciForm.blood_in_stool" :true-value="1" :false-value="0" /> Blood in stool</label>
                  </div>
                  <div class="hms-chip-row mt-2">
                    <label v-for="opt in dehydrationOpts" :key="opt" class="hms-chip">
                      <input type="checkbox" :value="opt" v-model="imciDehArr" /> {{ opt.replace(/_/g,' ') }}
                    </label>
                  </div>
                </div>

                <h6 class="mt-3 hms-imci-section"><i class="mdi mdi-thermometer"></i> Fever</h6>
                <label class="hms-check"><input type="checkbox" v-model="imciForm.fever_present" :true-value="1" :false-value="0" /> Fever</label>
                <div v-if="imciForm.fever_present==1" class="hms-form-grid">
                  <label>Days <input type="number" v-model="imciForm.fever_days" class="hms-input" /></label>
                  <label>RDT result
                    <select v-model="imciForm.rdt_result" class="hms-input">
                      <option value="not_done">Not done</option>
                      <option value="positive">Positive</option>
                      <option value="negative">Negative</option>
                    </select>
                  </label>
                  <label class="hms-check"><input type="checkbox" v-model="imciForm.stiff_neck" :true-value="1" :false-value="0" /> Stiff neck</label>
                  <label class="hms-check"><input type="checkbox" v-model="imciForm.generalized_rash" :true-value="1" :false-value="0" /> Rash</label>
                </div>

                <h6 class="mt-3 hms-imci-section"><i class="mdi mdi-ear-hearing"></i> Ear</h6>
                <label class="hms-check"><input type="checkbox" v-model="imciForm.ear_problem" :true-value="1" :false-value="0" /> Ear problem</label>
                <div v-if="imciForm.ear_problem==1" class="hms-form-grid">
                  <label class="hms-check"><input type="checkbox" v-model="imciForm.ear_pain" :true-value="1" :false-value="0" /> Pain</label>
                  <label class="hms-check"><input type="checkbox" v-model="imciForm.ear_discharge" :true-value="1" :false-value="0" /> Discharge</label>
                  <label>Discharge days <input type="number" v-model="imciForm.ear_discharge_days" class="hms-input" /></label>
                  <label class="hms-check"><input type="checkbox" v-model="imciForm.tender_swelling_behind_ear" :true-value="1" :false-value="0" /> Mastoid swelling</label>
                </div>

                <h6 class="mt-3 hms-imci-section"><i class="mdi mdi-water-percent"></i> Anaemia + Nutrition</h6>
                <div class="hms-form-grid">
                  <label>Palmar pallor
                    <select v-model="imciForm.palmar_pallor" class="hms-input">
                      <option value="not_assessed">Not assessed</option>
                      <option value="none">None</option>
                      <option value="some">Some (mild)</option>
                      <option value="severe">Severe</option>
                    </select>
                  </label>
                  <label class="hms-check"><input type="checkbox" v-model="imciForm.visible_severe_wasting" :true-value="1" :false-value="0" /> Visible severe wasting</label>
                  <label>Oedema
                    <select v-model="imciForm.bilateral_pitting_oedema" class="hms-input">
                      <option value="none">None</option>
                      <option value="grade_1">Grade 1 (feet only)</option>
                      <option value="grade_2">Grade 2 (feet + legs)</option>
                      <option value="grade_3">Grade 3 (generalized)</option>
                    </select>
                  </label>
                  <label>MUAC (cm) <input type="number" step="0.1" v-model="imciForm.muac_cm" class="hms-input" /></label>
                  <label>Appetite test
                    <select v-model="imciForm.appetite_test" class="hms-input">
                      <option value="not_done">Not done</option>
                      <option value="passed">Passed</option>
                      <option value="failed">Failed</option>
                    </select>
                  </label>
                  <label>HIV status known
                    <select v-model="imciForm.hiv_status_known" class="hms-input">
                      <option value="not_assessed">Not asked</option>
                      <option value="unknown">Unknown</option>
                      <option value="known_negative">Negative</option>
                      <option value="known_exposed">Exposed (HEI)</option>
                      <option value="known_positive">Positive</option>
                    </select>
                  </label>
                </div>

                <h6 class="mt-3 hms-imci-section"><i class="mdi mdi-pill"></i> Treatments given today</h6>
                <div class="hms-chip-row">
                  <label v-for="opt in treatmentOpts" :key="opt" class="hms-chip">
                    <input type="checkbox" :value="opt" v-model="imciTxArr" /> {{ opt.replace(/_/g,' ') }}
                  </label>
                </div>

                <div class="mt-3 hms-form-grid">
                  <label>Follow-up date <input type="date" v-model="imciForm.follow_up_date" class="hms-input" /></label>
                  <label>Referral reason <input v-model="imciForm.referral_reason" class="hms-input" /></label>
                </div>
                <label class="d-block mt-2">Notes <textarea v-model="imciForm.notes" rows="2" class="hms-input"></textarea></label>
              </div>
              <div class="hms-modal-foot">
                <button class="hms-btn hms-btn-secondary" @click="imciModal=false">Cancel</button>
                <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveImci">{{ saving ? 'Saving…' : 'Save assessment' }}</button>
              </div>
            </div>
          </div>
        </div>

        <!-- ============ NUTRITION / SAM / RUTF TAB ============ -->
        <div v-if="tab==='nutrition'">
          <div v-if="!sam.case" class="hms-muted text-center hms-pad">
            <i class="mdi mdi-food-apple-outline" style="font-size:48px"></i>
            <p>No active SAM/MAM case. Record an IMCI sick visit and the system will auto-enrol if SAM/MAM is classified.</p>
          </div>
          <div v-else>
            <div class="hms-card mb-3">
              <div class="hms-card-head"><i class="mdi mdi-food-apple"></i> {{ sam.case.classification.replace(/_/g,' ') }} — track: {{ sam.case.track }}</div>
              <div class="hms-card-body">
                <div class="hms-grid-2">
                  <div>
                    <div class="hms-row"><span class="hms-row-k">Enrolment date</span><span class="hms-row-v">{{ formatDate(sam.case.enrolment_date) }} ({{ sam.case.age_months_at_enrolment }}mo)</span></div>
                    <div class="hms-row"><span class="hms-row-k">Baseline weight</span><span class="hms-row-v">{{ sam.case.weight_kg }} kg</span></div>
                    <div class="hms-row"><span class="hms-row-k">Baseline MUAC</span><span class="hms-row-v">{{ sam.case.muac_cm || '—' }} cm</span></div>
                    <div class="hms-row"><span class="hms-row-k">Baseline WHZ</span><span class="hms-row-v">{{ sam.case.whz || '—' }}</span></div>
                    <div class="hms-row"><span class="hms-row-k">Oedema</span><span class="hms-row-v">{{ sam.case.has_oedema }}</span></div>
                  </div>
                  <div>
                    <div class="hms-row"><span class="hms-row-k">Target weight (discharge)</span><span class="hms-row-v"><strong>{{ sam.case.target_weight_kg || '—' }} kg</strong></span></div>
                    <div class="hms-row"><span class="hms-row-k">Appetite at baseline</span><span class="hms-row-v">{{ sam.case.appetite_test_baseline }}</span></div>
                    <div class="hms-row"><span class="hms-row-k">Status</span><span class="hms-row-v"><span class="hms-pill" :class="samStatusPill(sam.case.status)">{{ sam.case.status }}</span></span></div>
                    <div v-if="sam.case.complications" class="hms-row"><span class="hms-row-k">Complications</span><span class="hms-row-v">{{ sam.case.complications.replace(/,/g, ' · ').replace(/_/g, ' ') }}</span></div>
                  </div>
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-between mb-2">
              <h5 class="hms-h4">Visits + RUTF dispensing</h5>
              <button v-if="sam.case.status==='active'" class="hms-btn hms-btn-blue hms-btn-sm" @click="openSamVisitModal"><i class="mdi mdi-plus"></i> New visit</button>
            </div>
            <table class="hms-table">
              <thead><tr><th>Date</th><th>Weeks</th><th>Weight</th><th>MUAC</th><th>Oedema</th><th>Appetite</th><th>Gain g/kg/d</th><th>RUTF sachets</th><th>Decision</th></tr></thead>
              <tbody>
                <tr v-for="v in sam.visits" :key="v.sv_id" :class="samVisitClass(v)">
                  <td>{{ formatDate(v.visit_date) }}</td>
                  <td>{{ v.weeks_in_program }}</td>
                  <td>{{ v.weight_kg }} kg</td>
                  <td>{{ v.muac_cm || '—' }}</td>
                  <td>{{ v.oedema_status }}</td>
                  <td>{{ v.appetite_test }}</td>
                  <td><span :class="zPill(v.weight_gain_g_kg_day === null ? null : (v.weight_gain_g_kg_day >= 5 ? 2 : (v.weight_gain_g_kg_day >= 2 ? 0 : -2)))">{{ v.weight_gain_g_kg_day !== null ? v.weight_gain_g_kg_day : '—' }}</span></td>
                  <td><strong>{{ v.rutf_sachets_dispensed }}</strong> <span class="hms-muted-sm">({{ v.sachets_per_day_target }}/d)</span></td>
                  <td><span class="hms-pill" :class="samDecisionPill(v.decision)">{{ v.decision.replace(/_/g,' ') }}</span></td>
                </tr>
                <tr v-if="!sam.visits.length"><td colspan="9" class="text-center hms-pad hms-muted">No follow-up visits yet.</td></tr>
              </tbody>
            </table>

            <!-- SAM visit modal -->
            <div v-if="samVisitModal" class="hms-modal-bg" @click.self="samVisitModal=false">
              <div class="hms-modal">
                <div class="hms-modal-head">
                  <h5><i class="mdi mdi-scale-bathroom"></i> SAM follow-up visit</h5>
                  <button class="hms-btn hms-btn-secondary" @click="samVisitModal=false"><i class="mdi mdi-close"></i></button>
                </div>
                <div class="hms-modal-body">
                  <div class="hms-form-grid">
                    <label>Visit date <input type="date" v-model="samVisitForm.visit_date" class="hms-input" /></label>
                    <label>Weight (kg) <input type="number" step="0.01" v-model="samVisitForm.weight_kg" class="hms-input" /></label>
                    <label>Height (cm) <input type="number" step="0.1" v-model="samVisitForm.height_cm" class="hms-input" /></label>
                    <label>MUAC (cm) <input type="number" step="0.1" v-model="samVisitForm.muac_cm" class="hms-input" /></label>
                    <label>WHZ <input type="number" step="0.01" v-model="samVisitForm.whz" class="hms-input" /></label>
                    <label>Oedema
                      <select v-model="samVisitForm.oedema_status" class="hms-input">
                        <option value="none">None</option>
                        <option value="grade_1">Grade 1</option>
                        <option value="grade_2">Grade 2</option>
                        <option value="grade_3">Grade 3</option>
                      </select>
                    </label>
                    <label>Appetite test
                      <select v-model="samVisitForm.appetite_test" class="hms-input">
                        <option value="not_done">Not done</option>
                        <option value="passed">Passed</option>
                        <option value="failed">Failed</option>
                      </select>
                    </label>
                  </div>
                  <h6 class="mt-2">Complications</h6>
                  <div class="hms-chip-row">
                    <label v-for="opt in samComplicationOpts" :key="opt" class="hms-chip">
                      <input type="checkbox" :value="opt" v-model="samComplArr" /> {{ opt.replace(/_/g,' ') }}
                    </label>
                  </div>
                  <h6 class="mt-2">Drugs given today</h6>
                  <div class="hms-chip-row">
                    <label v-for="opt in samDrugOpts" :key="opt" class="hms-chip">
                      <input type="checkbox" :value="opt" v-model="samDrugsArr" /> {{ opt.replace(/_/g,' ') }}
                    </label>
                  </div>
                  <label class="d-block mt-2">RUTF sachets to dispense <input type="number" v-model="samVisitForm.rutf_sachets_dispensed" class="hms-input" placeholder="auto-calc if left blank" /></label>
                  <label class="d-block mt-2">Notes <textarea v-model="samVisitForm.notes" rows="2" class="hms-input"></textarea></label>
                </div>
                <div class="hms-modal-foot">
                  <button class="hms-btn hms-btn-secondary" @click="samVisitModal=false">Cancel</button>
                  <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveSamVisit">{{ saving ? 'Saving…' : 'Save visit' }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============ IMMUNIZATION TAB ============ -->
        <div v-if="tab==='kepi'">
          <div v-if="!kepi.client" class="hms-card mb-3">
            <div class="hms-card-body text-center">
              <p class="hms-muted">No CWC (Child Welfare Clinic) record yet.</p>
              <button class="hms-btn hms-btn-blue" @click="createCwc"><i class="mdi mdi-plus"></i> Open CWC record</button>
            </div>
          </div>
          <div v-else>
            <div class="hms-card mb-3">
              <div class="hms-card-head"><i class="mdi mdi-card-account-details"></i> CWC client</div>
              <div class="hms-card-body">
                <div class="hms-row"><span class="hms-row-k">CWC number</span><span class="hms-row-v"><strong>{{ kepi.client.cwc_number }}</strong></span></div>
                <div class="hms-row"><span class="hms-row-k">Status</span><span class="hms-row-v"><span class="hms-pill" :class="kepi.client.status==='active' ? 'hms-pill-success' : 'hms-pill-muted'">{{ kepi.client.status }}</span></span></div>
                <div class="hms-row"><span class="hms-row-k">Village</span><span class="hms-row-v">{{ kepi.client.village || '—' }}</span></div>
                <div class="hms-row"><span class="hms-row-k">CHV</span><span class="hms-row-v">{{ kepi.client.chv_name || '—' }} <span v-if="kepi.client.chv_phone" class="hms-muted-sm">· {{ kepi.client.chv_phone }}</span></span></div>
              </div>
            </div>

            <div class="hms-card">
              <div class="hms-card-head"><i class="mdi mdi-needle"></i> KEPI doses</div>
              <div class="hms-card-body">
                <a class="hms-btn hms-btn-blue hms-btn-sm" :href="'#/immunization?patient_id=' + patientId"><i class="mdi mdi-open-in-new"></i> Open in Immunization module</a>
                <table class="hms-table mt-2" v-if="kepi.doses && kepi.doses.length">
                  <thead><tr><th>Vaccine</th><th>Due age</th><th>Scheduled</th><th>Given</th><th>Status</th></tr></thead>
                  <tbody>
                    <tr v-for="d in kepi.doses" :key="d.dose_id">
                      <td><strong>{{ d.vaccine_code || d.vaccine_name }}</strong> <span class="hms-muted-sm">{{ d.vaccine_name }}</span></td>
                      <td class="hms-muted-sm">{{ d.schedule_age_weeks }}w</td>
                      <td>{{ formatDate(d.scheduled_date) || '—' }}</td>
                      <td>{{ formatDate(d.given_date) || '—' }}</td>
                      <td><span class="hms-pill" :class="kepiStatusPill(d.status)">{{ d.status }}</span></td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="hms-muted text-center hms-pad">No doses recorded yet. Open in Immunization module to schedule.</div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "NewbornRecord",
  components: { Navigation },
  data() {
    return {
      patientId: null, tab: 'birth', saving: false,
      record: { patient: null, delivery: null, mother_pmtct: null, mother_anc_latest: null, hei: null, hei_tests: [], parents: [], siblings: [] },
      // Growth
      growthIndicator: 'weight_for_age',
      growth: { measurements: [], reference_weight_for_age: [], reference_length_for_age: [], reference_head_circ_for_age: [], patient: {} },
      growthModal: false, growthForm: this.blankGrowthForm(),
      // Milestones
      milestones: { age_months: null, rows: [] },
      // KEPI
      kepi: { client: null, doses: [] },
      // IMCI
      imci: [], imciModal: false, imciForm: this.blankImciForm(),
      imciDangerArr: [], imciDehArr: [], imciTxArr: [],
      dangerSignsOpts: ['unable_to_drink_or_breastfeed','vomits_everything','convulsions_current','convulsions_history','lethargic_or_unconscious','stridor_in_calm_child','severe_chest_indrawing'],
      dehydrationOpts: ['lethargic_or_unconscious','restless_or_irritable','sunken_eyes','drinks_eagerly_thirsty','drinks_poorly','skin_pinch_very_slow','skin_pinch_slow'],
      treatmentOpts: ['artemether_lumefantrine','paracetamol','amoxicillin','ors','zinc','vitamin_A','mebendazole_albendazole','iron','rutf_started','iv_fluids','oxygen','breastfeeding_counselling','dehydration_plan_A','dehydration_plan_B','dehydration_plan_C','referral_drugs','immunization_given'],
      // SAM / RUTF
      sam: { case: null, visits: [] },
      samVisitModal: false, samVisitForm: this.blankSamVisitForm(),
      samComplArr: [], samDrugsArr: [],
      samComplicationOpts: ['vomiting','high_fever','hypothermia','severe_pneumonia','dehydration','convulsions','lethargy','anorexia'],
      samDrugOpts: ['amoxicillin','vitamin_A','deworming','folic_acid','iron','measles_vaccine']
    };
  },
  computed: {
    ageDisplay() {
      if (!this.record.patient || !this.record.patient.dob) return '';
      const dob = new Date((this.record.patient.dob + '').replace(' ', 'T'));
      const now = new Date();
      const months = (now.getFullYear() - dob.getFullYear()) * 12 + (now.getMonth() - dob.getMonth());
      if (months < 1) {
        const days = Math.floor((now - dob) / 86400000);
        return days + 'd';
      }
      if (months < 24) return months + 'mo';
      return Math.floor(months / 12) + 'y';
    },
    lowBirthWeight() {
      const w = this.record && this.record.delivery && this.record.delivery.baby_weight_g;
      return w && w < 2500;
    },
    growthFlag() {
      const list = this.growth.measurements || [];
      if (!list.length) return '';
      return list[list.length - 1].growth_status_flag;
    },
    referenceRows() {
      if (this.growthIndicator === 'weight_for_age') return this.growth.reference_weight_for_age || [];
      if (this.growthIndicator === 'length_for_age') return this.growth.reference_length_for_age || [];
      return this.growth.reference_head_circ_for_age || [];
    },
    yDomain() {
      const refs = this.referenceRows;
      if (!refs.length) return { min: 0, max: 25 };
      let lo = Infinity, hi = -Infinity;
      refs.forEach(r => { lo = Math.min(lo, Number(r.value_minus_2sd)); hi = Math.max(hi, Number(r.value_plus_2sd)); });
      const m = (this.growth.measurements || []);
      m.forEach(r => { const v = this.measurementValue(r); if (v !== null) { lo = Math.min(lo, v); hi = Math.max(hi, v); }});
      const pad = (hi - lo) * 0.1;
      return { min: Math.max(0, lo - pad), max: hi + pad };
    },
    xDomain() {
      return { min: 0, max: this.growthIndicator === 'head_circ_for_age' ? 24 : 60 };
    },
    measurementLine() {
      const m = this.growth.measurements || [];
      return m.map(r => {
        const v = this.measurementValue(r);
        if (v === null) return null;
        return this.xCoord(r.age_months_at_visit) + ',' + this.yCoord(v);
      }).filter(s => s).join(' ');
    },
    milestoneDomains() {
      return [
        { key:'gross_motor', label:'Gross motor', icon:'mdi mdi-run' },
        { key:'fine_motor', label:'Fine motor', icon:'mdi mdi-hand-pointing-up' },
        { key:'language', label:'Language', icon:'mdi mdi-message-text' },
        { key:'social_cognitive', label:'Social / Cognitive', icon:'mdi mdi-emoticon-happy' },
        { key:'self_care', label:'Self-care', icon:'mdi mdi-hand-water' }
      ];
    },
    redFlagCount() {
      return (this.milestones.rows || []).filter(m => m.effective_status === 'red_flag').length;
    },
    kepiOverdueCount() {
      return (this.kepi.doses || []).filter(d => d.status === 'overdue' || d.status === 'missed').length;
    },
    imciAgeBand() {
      const dob = this.record.patient && this.record.patient.dob;
      if (!dob) return '';
      const m = (Date.now() - new Date((dob + '').replace(' ','T')).getTime()) / 86400000 / 30.4375;
      return m < 2 ? 'young infant (0-2m)' : 'child (2m-5y)';
    }
  },
  mounted() {
    this.patientId = this.$route.params.id;
    this.load();
  },
  methods: {
    async load() {
      try {
        const r = await executeJson({ function: 'getBirthRecord', patient_id: this.patientId }, hms);
        if (r.data && r.data.success) this.record = r.data.data || {};
      } catch (e) { console.error(e); }
    },
    formatDate(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ', 'T')); return isNaN(dt) ? d : dt.toLocaleDateString(); },
    formatDateTime(d) { if (!d) return ''; const dt = new Date((d + '').replace(' ', 'T')); return isNaN(dt) ? d : dt.toLocaleString(); },
    riskPill(c) { return ({ critical:'hms-pill-danger', high:'hms-pill-warn', moderate:'hms-pill-info', low:'hms-pill-success' })[c] || 'hms-pill-muted'; },
    heiStatusPill(s) { return ({ on_followup:'hms-pill-info', final_negative:'hms-pill-success', positive:'hms-pill-danger', LTFU:'hms-pill-danger', transferred:'hms-pill-muted', died:'hms-pill-danger' })[s] || 'hms-pill-muted'; },
    dbsResultPill(r) { return ({ pending:'hms-pill-muted', negative:'hms-pill-success', positive:'hms-pill-danger', rejected:'hms-pill-warn', indeterminate:'hms-pill-warn' })[r] || 'hms-pill-muted'; },

    // ----- Growth -----
    blankGrowthForm() { return { measured_at: new Date().toISOString().substring(0,16),
      weight_kg:null, length_cm:null, height_cm:null, head_circumference_cm:null, muac_cm:null,
      source:'facility', notes:'' }; },
    async loadGrowth() {
      try {
        const r = await executeJson({ function:'getGrowthHistory', patient_id: this.patientId }, hms);
        if (r.data && r.data.success) this.growth = r.data.data || this.growth;
      } catch (e) { console.error(e); }
    },
    openGrowthModal() { this.growthForm = this.blankGrowthForm(); this.growthModal = true; },
    async saveGrowth() {
      this.saving = true;
      try {
        const r = await executeJson({ function:'recordGrowthMeasurement', patient_id: this.patientId, ...this.growthForm }, hms);
        if (r.data && r.data.success) {
          if (r.data.data && (r.data.data.nutritional_status||'').indexOf('severe') !== -1) {
            alert('SEVERE finding flagged: ' + r.data.data.nutritional_status.replace(/_/g,' ') + '. Critical alert opened.');
          }
          this.growthModal = false;
          await this.loadGrowth();
        } else if (r.data) alert(r.data.message || 'Save failed.');
      } finally { this.saving = false; }
    },
    measurementValue(m) {
      if (this.growthIndicator === 'weight_for_age') return m.weight_kg ? Number(m.weight_kg) : null;
      if (this.growthIndicator === 'length_for_age') return (m.length_cm || m.height_cm) ? Number(m.length_cm || m.height_cm) : null;
      if (this.growthIndicator === 'head_circ_for_age') return m.head_circumference_cm ? Number(m.head_circumference_cm) : null;
      return null;
    },
    xCoord(monthsRaw) {
      const months = Number(monthsRaw || 0);
      const d = this.xDomain;
      const clamped = Math.max(d.min, Math.min(d.max, months));
      return 40 + ((clamped - d.min) / (d.max - d.min || 1)) * 640;
    },
    yCoord(v) {
      const d = this.yDomain;
      return 330 - ((Number(v) - d.min) / (d.max - d.min || 1)) * 300;
    },
    refLine(which) {
      const refs = this.referenceRows;
      const xMax = this.xDomain.max;
      const key = which === 'p2' ? 'value_plus_2sd' : (which === 'med' ? 'value_median' : 'value_minus_2sd');
      return refs.filter(r => Number(r.age_months) <= xMax)
        .map(r => this.xCoord(r.age_months) + ',' + this.yCoord(Number(r[key])))
        .join(' ');
    },
    bandPath(zone) {
      // zone='upper' = above +2SD ; 'lower' = below -2SD
      const refs = this.referenceRows.filter(r => Number(r.age_months) <= this.xDomain.max);
      if (!refs.length) return '';
      if (zone === 'upper') {
        const top = refs.map(r => this.xCoord(r.age_months) + ',' + this.yCoord(Number(r.value_plus_2sd))).join(' L ');
        return 'M ' + this.xCoord(refs[0].age_months) + ',' + this.yCoord(this.yDomain.max) +
               ' L ' + top + ' L ' + this.xCoord(refs[refs.length-1].age_months) + ',' + this.yCoord(this.yDomain.max) + ' Z';
      } else {
        const bot = refs.map(r => this.xCoord(r.age_months) + ',' + this.yCoord(Number(r.value_minus_2sd))).join(' L ');
        return 'M ' + this.xCoord(refs[0].age_months) + ',' + this.yCoord(this.yDomain.min) +
               ' L ' + bot + ' L ' + this.xCoord(refs[refs.length-1].age_months) + ',' + this.yCoord(this.yDomain.min) + ' Z';
      }
    },
    yLabel(i) {
      const d = this.yDomain;
      const v = d.min + ((6 - i) / 5) * (d.max - d.min);
      return Math.round(v * 10) / 10;
    },
    indicatorLabel(i) { return ({ weight_for_age:'Weight-for-age (kg)', length_for_age:'Length / Height-for-age (cm)', head_circ_for_age:'Head circumference (cm)' })[i] || i; },
    growthRowClass(m) {
      const s = m.nutritional_status || '';
      if (s.indexOf('severe') !== -1) return 'row-action';
      if (s.indexOf('moderate') !== -1 || s.indexOf('mild') !== -1) return 'row-warn';
      return '';
    },
    zPill(z) {
      if (z === null || z === undefined || z === '') return '';
      const v = Number(z);
      if (v <= -3 || v >= 3) return 'hms-z-danger';
      if (v <= -2 || v >= 2) return 'hms-z-warn';
      return 'hms-z-good';
    },
    statusPill(s) { return ({ normal:'hms-pill-success', mild_wasting:'hms-pill-warn', moderate_wasting:'hms-pill-warn', severe_wasting:'hms-pill-danger',
      mild_stunting:'hms-pill-warn', moderate_stunting:'hms-pill-warn', severe_stunting:'hms-pill-danger',
      overweight:'hms-pill-warn', obese:'hms-pill-danger', at_risk:'hms-pill-warn', not_assessed:'hms-pill-muted' })[s] || 'hms-pill-muted'; },
    ageDisplayMonths(m) {
      const n = Number(m || 0);
      if (n < 1) return Math.floor(n * 30) + 'd';
      if (n < 24) return Math.round(n) + 'mo';
      return Math.floor(n / 12) + 'y';
    },

    // ----- Milestones -----
    async loadMilestones() {
      try {
        const r = await executeJson({ function:'getMilestones', patient_id: this.patientId }, hms);
        if (r.data && r.data.success) this.milestones = r.data.data || { age_months: null, rows: [] };
      } catch (e) { console.error(e); }
    },
    milestonesByDomain(key) { return (this.milestones.rows || []).filter(m => m.domain === key); },
    async setMilestone(code, status) {
      const r = await executeJson({ function:'recordMilestone', patient_id: this.patientId, code, status }, hms);
      if (r.data && r.data.success) await this.loadMilestones();
      else if (r.data) alert(r.data.message || 'Save failed.');
    },
    milestonePill(s) { return ({ achieved:'hms-pill-success', not_yet:'hms-pill-info', not_yet_due:'hms-pill-muted', due_for_check:'hms-pill-warn', overdue_unchecked:'hms-pill-warn', concern:'hms-pill-warn', red_flag:'hms-pill-danger' })[s] || 'hms-pill-muted'; },
    milestoneRowClass(m) {
      if (m.effective_status === 'red_flag') return 'row-action';
      if (m.effective_status === 'concern' || m.effective_status === 'overdue_unchecked') return 'row-warn';
      return '';
    },

    // ----- KEPI -----
    async loadKepi() {
      try {
        const r = await executeJson({ function:'getChildImmunizationStatus', patient_id: this.patientId }, hms);
        if (r.data && r.data.success) this.kepi = r.data.data || { client: null, doses: [] };
      } catch (e) { console.error(e); }
    },
    async createCwc() {
      const r = await executeJson({ function:'createImmunizationClientForBaby', patient_id: this.patientId }, hms);
      if (r.data && r.data.success) await this.loadKepi();
      else if (r.data) alert(r.data.message || 'Failed.');
    },
    kepiStatusPill(s) { return ({ given:'hms-pill-success', scheduled:'hms-pill-info', overdue:'hms-pill-warn', missed:'hms-pill-danger', refused:'hms-pill-warn', contraindicated:'hms-pill-muted' })[s] || 'hms-pill-muted'; },

    // ----- IMCI -----
    blankImciForm() { return { assessed_at: new Date().toISOString().substring(0,16), weight_kg:null, temperature_c:null,
      cough_present:0, cough_days:null, respiratory_rate:null, chest_indrawing:0, stridor:0, wheeze:0,
      diarrhoea_present:0, diarrhoea_days:null, blood_in_stool:0,
      fever_present:0, fever_days:null, rdt_result:'not_done', stiff_neck:0, runny_nose:0, generalized_rash:0,
      koplik_spots:0, mouth_ulcers:0, pus_draining_eye:0, corneal_clouding:0,
      ear_problem:0, ear_pain:0, ear_discharge:0, ear_discharge_days:null, tender_swelling_behind_ear:0,
      palmar_pallor:'not_assessed', visible_severe_wasting:0, bilateral_pitting_oedema:'none', muac_cm:null, appetite_test:'not_done',
      hiv_status_known:'not_assessed', final_action:'', referral_reason:'', follow_up_date:'', notes:'' }; },
    async loadImci() {
      const r = await executeJson({ function:'getImciHistory', patient_id: this.patientId }, hms);
      if (r.data && r.data.success) this.imci = r.data.data.rows || [];
    },
    openImciModal() {
      this.imciForm = this.blankImciForm();
      this.imciDangerArr = []; this.imciDehArr = []; this.imciTxArr = [];
      this.imciModal = true;
    },
    async saveImci() {
      this.saving = true;
      try {
        const payload = { function:'recordImciAssessment', patient_id: this.patientId, ...this.imciForm,
          danger_signs: this.imciDangerArr.length ? this.imciDangerArr.join(',') : 'none',
          dehydration_signs: this.imciDehArr.length ? this.imciDehArr.join(',') : 'none',
          treatments_given: this.imciTxArr.join(',') };
        const r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          if (r.data.data && r.data.data.sam_id) alert('SAM diagnosed — auto-enrolled. Open Nutrition tab.');
          else if (r.data.data && r.data.data.alerts && r.data.data.alerts.length) {
            alert('IMCI saved — ' + r.data.data.alerts.length + ' clinical alert(s) opened.');
          }
          this.imciModal = false;
          await this.loadImci();
          await this.loadSam();
        } else if (r.data) alert(r.data.message || 'Save failed.');
      } finally { this.saving = false; }
    },
    imciCardClass(a) {
      if (a.final_action === 'urgent_referral' || a.final_action === 'admit_inpatient') return 'hms-imci-critical';
      if (a.danger_signs && a.danger_signs !== 'none') return 'hms-imci-critical';
      return '';
    },
    imciPill(c) {
      if (!c) return 'hms-pill-muted';
      if (c.indexOf('severe') !== -1 || c.indexOf('very_severe') !== -1) return 'hms-pill-danger';
      if (['malaria','pneumonia','some_dehydration','anaemia','acute_ear_infection','measles','dysentery','moderate_acute_malnutrition'].indexOf(c) !== -1) return 'hms-pill-warn';
      if (c.indexOf('no_') === 0) return 'hms-pill-success';
      return 'hms-pill-info';
    },

    // ----- SAM / RUTF -----
    blankSamVisitForm() { return { visit_date: new Date().toISOString().substring(0,10),
      weight_kg:null, height_cm:null, muac_cm:null, whz:null, oedema_status:'none', appetite_test:'not_done',
      rutf_sachets_dispensed:null, notes:'' }; },
    async loadSam() {
      const r = await executeJson({ function:'getSamCaseDetail', patient_id: this.patientId }, hms);
      if (r.data && r.data.success) this.sam = r.data.data || { case:null, visits:[] };
    },
    openSamVisitModal() {
      this.samVisitForm = this.blankSamVisitForm();
      this.samComplArr = []; this.samDrugsArr = [];
      this.samVisitModal = true;
    },
    async saveSamVisit() {
      this.saving = true;
      try {
        const payload = { function:'recordSamVisit', sam_id: this.sam.case.sam_id, ...this.samVisitForm,
          medical_complications: this.samComplArr.length ? this.samComplArr.join(',') : 'none',
          drugs_given: this.samDrugsArr.length ? this.samDrugsArr.join(',') : 'none' };
        const r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          if (d.decision === 'discharge_cured') alert('Cured — child has met discharge criteria. Case closed.');
          else if (d.decision === 'transfer_to_SC') alert('TRANSFER to Stabilization Centre due to complications.');
          else alert('Visit saved. RUTF: ' + d.rutf_sachets_dispensed + ' sachets dispensed (target ' + d.sachets_per_day_target + '/day for ' + d.kcal_per_day + ' kcal/day).');
          this.samVisitModal = false;
          await this.loadSam();
        } else if (r.data) alert(r.data.message || 'Save failed.');
      } finally { this.saving = false; }
    },
    samStatusPill(s) { return ({ active:'hms-pill-warn', cured:'hms-pill-success', defaulted:'hms-pill-danger', died:'hms-pill-danger', non_responder:'hms-pill-danger', transferred:'hms-pill-muted', medical_referral:'hms-pill-danger' })[s] || 'hms-pill-muted'; },
    samDecisionPill(d) { return ({ continue_OTP:'hms-pill-info', discharge_cured:'hms-pill-success', transfer_to_SC:'hms-pill-danger', default:'hms-pill-danger', died:'hms-pill-danger' })[d] || 'hms-pill-muted'; },
    samVisitClass(v) { if (v.decision === 'transfer_to_SC' || v.decision === 'died') return 'row-action'; if (v.decision === 'discharge_cured') return 'row-success'; return ''; }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; }

.hms-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.hms-grid-2-1 { display: grid; grid-template-columns: 2fr 1fr; gap: 14px; }
@media (max-width: 900px) { .hms-grid-2, .hms-grid-2-1 { grid-template-columns: 1fr; } }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; font-size: 13px; color: #2d3748; display: flex; align-items: center; gap: 6px; }
.hms-card-body { padding: 12px 14px; }
.hms-card-pmtct .hms-card-head { background: #fef2f2; color: #7f1d1d; }

.hms-row { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; border-bottom: 1px solid #edf2f7; font-size: 13px; }
.hms-row:last-child { border-bottom: none; }
.hms-row-k { color: #718096; font-size: 12px; font-weight: 600; }
.hms-row-v { color: #2d3748; text-align: right; }

.hms-good { color: #22543d; font-size: 16px; }
.hms-bad  { color: #742a2a; font-size: 16px; }

.hms-fam-row { display: flex; gap: 10px; align-items: center; padding: 5px 0; font-size: 13px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; text-transform: capitalize; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-muted, .hms-muted-sm { color: #a0aec0; }
.hms-muted-sm { font-size: 11px; }
.hms-empty { padding: 80px 20px; text-align: center; color: #a0aec0; }
.hms-empty i { font-size: 48px; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.hms-table th { background: #f7fafc; padding: 6px 8px; font-weight: 600; color: #4a5568; text-align: left; border-bottom: 1px solid #e2e8f0; }
.hms-table td { padding: 5px 8px; border-bottom: 1px solid #edf2f7; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue { background: #2b6cb0; color: #fff; }
.hms-btn-success { background: #38a169; color: #fff; }
.hms-btn-warning { background: #d97706; color: #fff; }
.hms-btn-secondary { background: #edf2f7; color: #2d3748; border: 1px solid #cbd5e0; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 14px; flex-wrap: wrap; }
.hms-tab { padding: 10px 14px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 13px; display: inline-flex; align-items: center; gap: 6px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }

.hms-input { width: 100%; padding: 7px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.hms-form-grid label { display: block; font-size: 12px; color: #4a5568; font-weight: 600; }

.hms-growth-svg { width: 100%; height: 360px; background: #fff; }

.hms-z-good   { padding: 1px 6px; border-radius: 4px; background: #c6f6d5; color: #22543d; font-weight: 600; }
.hms-z-warn   { padding: 1px 6px; border-radius: 4px; background: #feebc8; color: #7b341e; font-weight: 600; }
.hms-z-danger { padding: 1px 6px; border-radius: 4px; background: #fed7d7; color: #742a2a; font-weight: 600; }

.row-warn    { background: #fffaf0; }
.row-action  { background: #fff5f5; }
.row-success { background: #f0fff4; }

.hms-chip-row { display: flex; flex-wrap: wrap; gap: 6px; }
.hms-chip { display: inline-flex; align-items: center; gap: 4px; background: #f7fafc; border: 1px solid #e2e8f0; padding: 4px 10px; border-radius: 14px; font-size: 12px; cursor: pointer; }
.hms-chip:has(input:checked) { background: #fed7d7; border-color: #fc8181; color: #742a2a; }
.hms-check { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; }

.hms-imci-section { font-size: 12px; color: #2d3748; font-weight: 700; text-transform: uppercase; letter-spacing: .3px; border-top: 1px solid #e2e8f0; padding-top: 8px; }
.hms-imci-row { padding: 6px 10px; border-radius: 4px; font-size: 12px; margin-bottom: 4px; }
.hms-imci-danger { background: #fef2f2; color: #7f1d1d; }
.hms-imci-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 12px; }
.hms-imci-critical .hms-card-head { background: #fef2f2; color: #7f1d1d; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.55); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; width: 640px; display: flex; flex-direction: column; overflow: hidden; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }

.hms-pad { padding: 16px; }
</style>
